import {
  array,
  boolean,
  discriminatedUnion,
  literal,
  nativeEnum,
  number,
  object,
  preprocess,
  string,
} from 'zod';

import { makeZodValidate as makeValidate } from '@occc-app/design-system';
import { IS_POSITIVE, IS_REQUIRED } from '@occc-app/lib/constants/validationMessages';
import { AwardType } from '@occc-app/offerHelpers/types';
import { Earning_Mechanism_Product_Counting_Attribute } from '@occc/graphql';
import {
  AwardPointsSchema,
  AwardVouchersWithExpirationDaysSchema,
  CustomDataSchema,
  OfferFulfillmentPrioritySchema,
  OfferNameAndDescriptionSchema,
  ProductGroupsSchema,
  VoucheredOfferSchema,
  withIsEnabled,
} from '@occc/validation';
import {
  awardRestrictionsSchema,
  FulfillmentChannelConstraintSchema,
  fulfillmentWindowSchema,
  PaymentMethodConstraintSchema,
  requiresActivationConstraintWithLimitsSchema,
} from '@occc/feat-constraints/schemas';
import {
  LimitDisabledSchemaWithIsEnabled,
  LimitSchema,
  progressPerPeriodLimitSchema,
  progressPerTransactionLimitSchema,
  transactionFrequencyLimitSchema,
  validateMembershipLessThanGlobal,
} from '@occc/feat-limits';

const ProgressLimitsSchema = {
  progressPerPeriod: progressPerPeriodLimitSchema.optional(),
  progressPerTransaction: progressPerTransactionLimitSchema.optional(),
};

const AwardCountEnabledSchema = object({
  membership: LimitSchema,
  global: LimitSchema,
  ...ProgressLimitsSchema,
  transactionFrequency: transactionFrequencyLimitSchema,
}).superRefine((data, ctx) => {
  validateMembershipLessThanGlobal({ membership: data.membership, global: data.global, ctx });
});

const AwardCountDisabledSchema = object({
  membership: preprocess(() => ({}), LimitDisabledSchemaWithIsEnabled),
  global: preprocess(() => ({}), LimitDisabledSchemaWithIsEnabled),
  ...ProgressLimitsSchema,
  transactionFrequency: preprocess(() => ({}), transactionFrequencyLimitSchema),
});

const AwardCountSchema = withIsEnabled(AwardCountEnabledSchema, AwardCountDisabledSchema);

const PunchcardChallengeSchema = object({
  products: array(
    object({
      purchaseThreshold: number({ required_error: IS_REQUIRED }).positive(IS_POSITIVE),
      eligibleProductGroups: ProductGroupsSchema,
      countProductsBy: nativeEnum(Earning_Mechanism_Product_Counting_Attribute),
    }),
  ),
  earningMechanism: literal('varietyPurchaseOverTime'),
  stayVisibleWhileInProgress: object({ isEnabled: boolean() }).optional(),
});

const BasePunchcardSchema = OfferNameAndDescriptionSchema.merge(
  OfferFulfillmentPrioritySchema,
).extend({
  earningMechanism: object({ id: string().optional() }),
  punchcardChallenge: preprocess((value) => value ?? {}, PunchcardChallengeSchema),
  awardCount: AwardCountSchema,
  activation: requiresActivationConstraintWithLimitsSchema,
  fulfillmentWindow: fulfillmentWindowSchema,
  fulfillmentChannelConstraint: FulfillmentChannelConstraintSchema,
  paymentMethodConstraint: PaymentMethodConstraintSchema,
  awardRestrictions: awardRestrictionsSchema,
  customData: CustomDataSchema,

  awardPoints: AwardPointsSchema.partial().optional(),
  awardVouchers: object({
    voucheredOffer: VoucheredOfferSchema.partial().passthrough().optional(),
    isEnabled: boolean().optional(),
    voucherExpirationDays: number().optional(),
  })
    .partial()
    .optional(),
});

const AwardPointsVariantSchema = object({
  awardType: literal(AwardType.AwardPoints),
  awardPoints: preprocess((value) => value ?? {}, AwardPointsSchema),
});

const AwardVouchersVariantSchema = object({
  awardType: literal(AwardType.AwardVouchers),
  awardVouchers: preprocess((value) => value ?? {}, AwardVouchersWithExpirationDaysSchema),
});

export const PunchcardConfigurationSchema = discriminatedUnion('awardType', [
  BasePunchcardSchema.merge(AwardPointsVariantSchema),
  BasePunchcardSchema.merge(AwardVouchersVariantSchema),
]);

export const validateConfiguration = makeValidate(PunchcardConfigurationSchema);
